// src/api.js
import { availableBookingTime, BookingDepositAction, BookingSettings, IBooking } from '@/@type/booking';
import { IGoogleIntegrationInfo } from '@/@type/google/feeds/settings';
import { Merchant } from '@/__generated__/types';
import { store } from '@/redux/store';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import qs from 'qs';
import { BookingAPIEndpoint, bookingAPIPath, HEADER_AUTHORIZATION, HEADER_CF_TURNSTILE_RESPONSE } from './consts';
import {
    BookingSettingsUpdatePayload,
    IAdminMessage,
    IBookingApiResponse,
    IBookingListQueryParams,
    IBookingOnboardingRequest,
    IBookingOnboardingResponse,
    IBookingOnboardingSubmission,
    IBookingReqBody,
    ICreateShortLinkRequest,
    ICreateShortLinkResponse,
    ICreateMessageRequest,
    IMerchantAreas,
    IMerchantPlanSubscription,
    IMerchantSubscriptionGlobal,
    IPlan,
    IRequestOptions,
    ISelectPlanBody,
    ISmsReportDetailsResponse,
    ISmsReportQueryParams,
    ISmsReportSummaryResponse,
    IUpdateBookingOnboardingRequest,
    IUpdateMessageRequest,
    IUserRecord,
    MerchantAreaBackup,
    MerchantShortLinks
} from './type';
const clientID = import.meta.env.VITE_BOOKING_CLIENT_ID || 'e818e73b-8f06-4d94-acdc-d7f7f2330ef7';

const axiosClient = axios.create({
    baseURL: import.meta.env.VITE_BOOKING_HOST,
    headers: {
        'Content-Type': 'application/json'
    }
});

export const setAccessToken = (token: string): void => {
    axiosClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

axiosClient.interceptors.request.use(
    async (config) => {
        config.headers.clientId = clientID;
        const state = store.getState();
        const token = state.auth.user?.loginToken;
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

async function request<TResponse, TBody = unknown, TParams = unknown>(
    options: IRequestOptions<TBody, TParams>
): Promise<IBookingApiResponse<TResponse>> {
    const { path, method, params, body, headers } = options;
    const fullPath = `${axiosClient.defaults.baseURL}${path.startsWith('/') ? path : `/${path}`}`;
    if (body && typeof body === 'object' && 'updatedAt' in body) {
        delete body.updatedAt;
    }
    const config: AxiosRequestConfig = {
        headers: headers,
        url: fullPath,
        method,
        params,
        data: body,
        paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
        }
    };
    try {
        const response = await axiosClient.request<IBookingApiResponse<TResponse>>(config);
        return response.data;
    } catch (error) {
        console.error('API request error:', error);
        const axiosError = error as AxiosError;
        const response = axiosError.response as AxiosResponse;
        console.error('request error', response);
        return response.data;
    }
}

export async function bookingMerchantQuery(merchantId: string): Promise<Merchant | undefined> {
    // build the request options
    const endPoint = bookingAPIPath.merchant.query(merchantId);
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method,
        params: {}
    };
    // call the request function
    const resp = await request<Merchant>(options);
    return resp.data;
}

export async function createBookingByCustomer(
    payload: IBookingReqBody,
    turnstileToken: string
): Promise<IBookingApiResponse<IBooking> | undefined> {
    if (!turnstileToken) {
        console.error('Turnstile token is required for booking update by customer.');
        const resp: IBookingApiResponse<IBooking> = {
            message: 'Turnstile token is required',
            debugMessage: 'Turnstile token is required'
        };
        return resp;
    }
    // build the request options
    const endPoint = bookingAPIPath.public.createBookingByCustomer(payload.merchantId);
    const options: IRequestOptions<IBookingReqBody> = {
        headers: {
            [HEADER_CF_TURNSTILE_RESPONSE]: turnstileToken
        },
        path: endPoint.url,
        method: endPoint.method,
        body: payload
    };
    // call the request function
    const resp = await request<IBooking, IBookingReqBody>(options);
    return resp;
}

export async function createBookingByStaff(payload: Partial<IBookingReqBody>): Promise<IBookingApiResponse<IBooking> | undefined> {
    // build the request options
    const endPoint = bookingAPIPath.booking.createBookingByStaff(payload.merchantId || '');
    const options: IRequestOptions<Partial<IBookingReqBody>> = {
        path: endPoint.url,
        method: endPoint.method,
        body: payload
    };
    // call the request function
    const resp = await request<IBooking, Partial<IBookingReqBody>>(options);
    return resp;
}

export async function submitBookingOnboarding(
    payload: IBookingOnboardingRequest,
    turnstileToken: string
): Promise<IBookingApiResponse<IBookingOnboardingResponse> | undefined> {
    if (!turnstileToken) {
        console.error('Turnstile token is required for booking onboarding submission.');
        const resp: IBookingApiResponse<IBookingOnboardingResponse> = {
            message: 'Turnstile token is required',
            debugMessage: 'Turnstile token is required'
        };
        return resp;
    }

    const endPoint = bookingAPIPath.public.submitBookingOnboarding();
    const options: IRequestOptions<IBookingOnboardingRequest> = {
        headers: {
            [HEADER_CF_TURNSTILE_RESPONSE]: turnstileToken
        },
        path: endPoint.url,
        method: endPoint.method,
        body: payload
    };

    return request<IBookingOnboardingResponse, IBookingOnboardingRequest>(options);
}

export async function queryBookingOnboardingSubmission(
    submissionId: string
): Promise<IBookingApiResponse<IBookingOnboardingSubmission> | undefined> {
    const endPoint = bookingAPIPath.booking.queryBookingOnboardingSubmission(submissionId);
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method
    };

    return request<IBookingOnboardingSubmission>(options);
}

export async function queryBookingOnboardingSubmissions(): Promise<IBookingApiResponse<IBookingOnboardingSubmission[]> | undefined> {
    const endPoint = bookingAPIPath.booking.queryBookingOnboardingSubmissions();
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method
    };

    return request<IBookingOnboardingSubmission[]>(options);
}

export async function updateBookingOnboardingSubmission(
    submissionId: string,
    payload: IUpdateBookingOnboardingRequest
): Promise<IBookingApiResponse<Partial<IBookingOnboardingSubmission>> | undefined> {
    const endPoint = bookingAPIPath.booking.updateBookingOnboardingSubmission(submissionId);
    const options: IRequestOptions<IUpdateBookingOnboardingRequest> = {
        path: endPoint.url,
        method: endPoint.method,
        body: payload
    };

    return request<Partial<IBookingOnboardingSubmission>, IUpdateBookingOnboardingRequest>(options);
}

export async function bookingPublicQuery(
    merchantId: string,
    yearStr: string,
    bookingNumber: string
): Promise<IBookingApiResponse<IBooking> | undefined> {
    // build the request options
    const endPoint = bookingAPIPath.public.queryPublicBooking(merchantId, yearStr, bookingNumber);
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method
    };
    // call the request function
    const resp = await request<IBooking>(options);
    return resp;
}

export async function bookingQueryByStaff(
    merchantId: string,
    yearStr: string,
    bookingNumber: string
): Promise<IBookingApiResponse<IBooking> | undefined> {
    // build the request options
    const endPoint = bookingAPIPath.booking.queryByStaff(merchantId, yearStr, bookingNumber);
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method
    };
    // call the request function
    const resp = await request<IBooking>(options);
    return resp;
}

export async function bookingDepositStatusUpdate(
    merchantId: string,
    yearStr: string,
    bookingNumber: string,
    depositAction: BookingDepositAction
): Promise<IBookingApiResponse<IBooking> | undefined> {
    // build the request options
    const endPoint = bookingAPIPath.booking.updateBookingDepositStatus(merchantId, yearStr, bookingNumber);
    const options: IRequestOptions<{ depositAction: BookingDepositAction }> = {
        path: endPoint.url,
        method: endPoint.method,
        body: { depositAction }
    };
    // call the request function
    const resp = await request<IBooking>(options);
    return resp;
}

export async function bookingUpdateByStaff(payload: Partial<IBooking>): Promise<IBookingApiResponse<boolean> | undefined> {
    const payloadCopy = cloneDeep(payload);
    if (payloadCopy.staffLabel === null) {
        delete payloadCopy.staffLabel; // remove staffLabel if it is null
    }
    delete payloadCopy.updateLogs;
    // build the request options
    const endPoint = bookingAPIPath.booking.updateByStaff(payloadCopy?.merchantId || '');
    const options: IRequestOptions<Partial<IBooking>> = {
        path: endPoint.url,
        method: endPoint.method,
        body: payloadCopy
    };
    // call the request function
    const resp = await request<boolean>(options);
    return resp;
}

export async function bookingUpdateByCustomer(
    payload: Partial<IBooking>,
    turnstileToken?: string,
    token?: string
): Promise<IBookingApiResponse<IBooking | null>> {
    if (!turnstileToken) {
        console.error('Turnstile token is required for booking update by customer.');
        const resp: IBookingApiResponse<null> = {
            message: 'Turnstile token is required',
            debugMessage: 'Turnstile token is required',
            data: null
        };
        return resp;
    }
    if (!token) {
        console.error('Authorization token is required for booking update by customer.');
        const resp: IBookingApiResponse<Partial<null>> = {
            message: 'Authorization token is required',
            debugMessage: 'Authorization token is required',
            data: null
        };
        return resp;
    }
    // build the request options
    const endPoint = bookingAPIPath.public.updateBookingByCustomer(payload?.merchantId || '');
    const options: IRequestOptions<Partial<IBooking>> = {
        headers: {
            [HEADER_CF_TURNSTILE_RESPONSE]: turnstileToken,
            [HEADER_AUTHORIZATION]: `Bearer ${token}`
        },
        path: endPoint.url,
        method: endPoint.method,
        body: payload
    };
    // call the request function
    const resp = await request<IBooking>(options);
    return resp;
}

async function queryBookingAvailableTimes(
    endPoint: BookingAPIEndpoint,
    timestamp: number,
    timezone: string,
    partySize?: number,
    excludeBookingNumber?: string,
    bookingTags?: string[]
): Promise<IBookingApiResponse<availableBookingTime[]> | undefined> {
    const params: Record<string, string | number | string[]> = { timestamp };

    if (partySize !== undefined) {
        params.partySize = partySize;
    }
    if (excludeBookingNumber !== undefined) {
        params.excludeBookingNumber = excludeBookingNumber;
    }
    if (timezone) {
        params.timezone = timezone;
    }
    if (bookingTags && bookingTags.length > 0) {
        params.bookingTags = bookingTags;
    }

    // build the request options
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method,
        params
    };

    // call the request function
    const resp = await request<availableBookingTime[]>(options);
    return resp;
}

export async function bookingAvailableTimesQuery(
    merchantId: string,
    timestamp: number,
    timezone: string,
    partySize?: number,
    excludeBookingNumber?: string,
    bookingTags?: string[]
): Promise<IBookingApiResponse<availableBookingTime[]> | undefined> {
    const endPoint = bookingAPIPath.booking.queryBookingAvailableTime(merchantId);
    return queryBookingAvailableTimes(endPoint, timestamp, timezone, partySize, excludeBookingNumber, bookingTags);
}

export async function bookingAvailableTimesQueryByCustomer(
    merchantId: string,
    timestamp: number,
    timezone: string,
    partySize?: number,
    excludeBookingNumber?: string,
    bookingTags?: string[]
): Promise<IBookingApiResponse<availableBookingTime[]> | undefined> {
    const endPoint = bookingAPIPath.public.queryBookingAvailableTime(merchantId);
    return queryBookingAvailableTimes(endPoint, timestamp, timezone, partySize, excludeBookingNumber, bookingTags);
}

export async function bookingPublicMerchantQuery(merchantId: string): Promise<Merchant | undefined> {
    // build the request options
    const endPoint = bookingAPIPath.public.queryMerchant(merchantId);
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method
    };
    // call the request function
    const resp = await request<Merchant>(options);
    return resp.data;
}

export async function bookingMerchantBookingSettingsUpdate(
    merchantId: string,
    bookingSettings: BookingSettingsUpdatePayload
): Promise<BookingSettings | undefined> {
    // build the request options
    const endPoint = bookingAPIPath.booking.updateBookingSettings(merchantId);
    const options: IRequestOptions<BookingSettingsUpdatePayload> = {
        path: endPoint.url,
        method: endPoint.method,
        body: bookingSettings
    };
    // call the request function
    const resp = await request<BookingSettings>(options);
    return resp.data;
}
/**
 * Fetches the list of bookings for a given merchant and query parameters.
 *
 * @param {string} merchantId - The ID of the merchant.
 * @param {Object} queryParams - The query parameters.
 * @returns {Promise<IBooking[] | undefined>} - A promise that resolves to an array of bookings or undefined.
 */
async function fetchBookingList(merchantId: string, queryParams: IBookingListQueryParams): Promise<IBooking[] | undefined> {
    // build the request options
    const endPoint = bookingAPIPath.booking.queryBookingList(merchantId);
    const options: IRequestOptions<IBooking[], IBookingListQueryParams> = {
        path: endPoint.url,
        method: endPoint.method,
        params: queryParams
    };
    // call the request function
    const resp = await request<IBooking[]>(options);
    return resp.data;
}

// export async function bookingListQuery(merchantId: string, queryParams: IBookingListQueryParams): Promise<IBooking[] | undefined> {
//     return fetchBookingList(merchantId, queryParams);
// }

/**
 * Fetches the list of bookings for a given merchant and month.
 *
 * @param {string} merchantId - The ID of the merchant.
 * @param {string} monthString - The month string in ISO 8601 format.
 * @returns {Promise<IBooking[] | undefined>} - A promise that resolves to an array of bookings or undefined.
 */
export async function bookingListByMonthQuery(merchantId: string, monthString: string): Promise<IBooking[] | undefined> {
    return fetchBookingList(merchantId, { monthString });
}

export async function bookingMerchantBookingSettingsQuery(merchantId: string): Promise<BookingSettings | undefined> {
    // build the request options
    const endPoint = bookingAPIPath.booking.queryBookingSettings(merchantId);
    const options: IRequestOptions<BookingSettings> = {
        path: endPoint.url,
        method: endPoint.method
    };
    // call the request function
    const resp = await request<BookingSettings>(options);
    return resp.data;
}

export async function bookingSmsReportQuery(queryParams: ISmsReportQueryParams): Promise<ISmsReportSummaryResponse | undefined> {
    const endPoint = bookingAPIPath.utils.smsReport();
    const options: IRequestOptions<undefined, ISmsReportQueryParams> = {
        path: endPoint.url,
        method: endPoint.method,
        params: queryParams
    };
    const resp = await request<ISmsReportSummaryResponse, undefined, ISmsReportQueryParams>(options);
    return resp.data;
}

export async function bookingSmsReportDetailsQuery(queryParams: ISmsReportQueryParams): Promise<ISmsReportDetailsResponse | undefined> {
    const endPoint = bookingAPIPath.utils.smsReportDetails();
    const options: IRequestOptions<undefined, ISmsReportQueryParams> = {
        path: endPoint.url,
        method: endPoint.method,
        params: queryParams
    };
    const resp = await request<ISmsReportDetailsResponse, undefined, ISmsReportQueryParams>(options);
    return resp.data;
}

export async function merchantAreasQuery(merchantId: string): Promise<IBookingApiResponse<IMerchantAreas> | undefined> {
    // build the request options
    const endPoint = bookingAPIPath.merchant.areas.query(merchantId);
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method
    };
    // call the request function
    const resp = await request<IMerchantAreas>(options);
    return resp;
}

export async function merchantAreasInit(merchantId: string): Promise<IBookingApiResponse<IMerchantAreas> | undefined> {
    // build the request options
    const endPoint = bookingAPIPath.merchant.areas.init(merchantId);
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method
    };
    // call the request function
    const resp = await request<IMerchantAreas>(options);
    return resp;
}

export async function merchantAreasUpdate(
    merchantId: string,
    payload: IMerchantAreas
): Promise<IBookingApiResponse<IMerchantAreas> | undefined> {
    // build the request options
    const endPoint = bookingAPIPath.merchant.areas.updateMerchantAreas(merchantId);
    const options: IRequestOptions<IMerchantAreas> = {
        path: endPoint.url,
        method: endPoint.method,
        body: payload
    };
    // call the request function
    const resp = await request<IMerchantAreas>(options);
    return resp;
}

export async function merchantAreaBackupsQuery(
    merchantId: string
): Promise<IBookingApiResponse<{ backups: MerchantAreaBackup[] }> | undefined> {
    const endPoint = bookingAPIPath.merchant.areaBackups.query(merchantId);
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method
    };
    const resp = await request<{ backups: MerchantAreaBackup[] }>(options);
    return resp;
}

export async function merchantAreaBackupRollback(
    merchantId: string,
    backupId: string
): Promise<IBookingApiResponse<IMerchantAreas> | undefined> {
    const endPoint = bookingAPIPath.merchant.areaBackups.rollback(merchantId, backupId);
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method
    };
    const resp = await request<IMerchantAreas>(options);
    return resp;
}

export async function merchantAreaBackupDelete(
    merchantId: string,
    backupId: string
): Promise<IBookingApiResponse<boolean> | undefined> {
    const endPoint = bookingAPIPath.merchant.areaBackups.delete(merchantId, backupId);
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method
    };
    const resp = await request<boolean>(options);
    return resp;
}

export async function queryMerchantShortLinks(merchantId: string): Promise<IBookingApiResponse<MerchantShortLinks>> {
    // build the request options
    const endPoint = bookingAPIPath.merchant.queryMerchantShortLinks(merchantId);
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method
    };
    // call the request function
    const resp = await request<MerchantShortLinks>(options);
    return resp;
}

export async function createShortLink(payload: ICreateShortLinkRequest): Promise<IBookingApiResponse<ICreateShortLinkResponse>> {
    const endPoint = bookingAPIPath.utils.createShortLink();
    const options: IRequestOptions<ICreateShortLinkRequest> = {
        path: endPoint.url,
        method: endPoint.method,
        body: payload
    };
    const resp = await request<ICreateShortLinkResponse, ICreateShortLinkRequest>(options);
    return resp;
}

export async function queryGoogleSettings(merchantId: string): Promise<IBookingApiResponse<IGoogleIntegrationInfo>> {
    // build the request options
    const endPoint = bookingAPIPath.google.queryGoogleSettings(merchantId);
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method
    };
    // call the request function
    const resp = await request<IGoogleIntegrationInfo>(options);
    return resp;
}

export async function queryGooglePublicSettings(merchantId: string): Promise<IBookingApiResponse<IGoogleIntegrationInfo>> {
    // build the request options
    const endPoint = bookingAPIPath.google.queryGooglePublicSettings(merchantId);
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method
    };
    // call the request function
    const resp = await request<IGoogleIntegrationInfo>(options);
    return resp;
}

export async function queryUserRecords(merchantId?: string): Promise<IBookingApiResponse<IUserRecord[]>> {
    // build the request options
    const endPoint = bookingAPIPath.users.queryUserRecords();
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method
    };
    if (merchantId) {
        options.params = { merchantId };
    }
    // call the request function
    const resp = await request<IUserRecord[]>(options);
    return resp;
}

export async function updateGoogleInfo(
    merchantId: string,
    info: IGoogleIntegrationInfo
): Promise<IBookingApiResponse<IGoogleIntegrationInfo>> {
    // build the request options
    const endPoint = bookingAPIPath.google.updateGoogleSettings(merchantId);
    const options: IRequestOptions<IGoogleIntegrationInfo> = {
        path: endPoint.url,
        method: endPoint.method,
        body: info
    };
    // call the request function
    const resp = await request<IGoogleIntegrationInfo>(options);
    return resp;
}

export async function updateGoogleDailyFeeds(): Promise<IBookingApiResponse<boolean>> {
    const endPoint = bookingAPIPath.google.updateGoogleDailyFeeds();
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method
    };
    // call the request function
    const resp = await request<boolean>(options);
    return resp;
}

export async function queryGoogleDailyFeeds(): Promise<IBookingApiResponse<unknown>> {
    const endPoint = bookingAPIPath.google.queryGoogleDailyFeeds();
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method
    };
    // call the request function
    const resp = await request<unknown>(options);
    return resp;
}

export async function replaceGoogleAvailability(merchantId: string): Promise<IBookingApiResponse<unknown>> {
    const endPoint = bookingAPIPath.google.replaceGoogleAvailability();
    const options: IRequestOptions<{ merchantId: string }> = {
        path: endPoint.url,
        method: endPoint.method,
        body: { merchantId }
    };
    const resp = await request<unknown>(options);
    return resp;
}

export async function downloadBookingCalendar(merchantId: string, yearStr: string, bookingNumber: string, token: string): Promise<Blob> {
    const endPoint = bookingAPIPath.public.downloadCalendar(merchantId);

    try {
        const response = await axiosClient.request({
            url: endPoint.url,
            method: endPoint.method,
            params: {
                yearStr,
                bookingNumber,
                token
            },
            responseType: 'blob',
            headers: {
                clientId: clientID
            }
        });
        return response.data;
    } catch (error) {
        console.error('Calendar download error:', error);
        throw error;
    }
}

export async function createAdminMessage(payload: ICreateMessageRequest): Promise<IBookingApiResponse<IAdminMessage>> {
    const endPoint = bookingAPIPath.message.createMessage();
    const options: IRequestOptions<ICreateMessageRequest> = {
        path: endPoint.url,
        method: endPoint.method,
        body: payload
    };
    const resp = await request<IAdminMessage, ICreateMessageRequest>(options);
    return resp;
}

export async function queryAdminMessages(): Promise<IBookingApiResponse<IAdminMessage[]>> {
    const endPoint = bookingAPIPath.message.queryMessages();
    const options: IRequestOptions = {
        path: endPoint.url,
        method: endPoint.method
    };
    const resp = await request<IAdminMessage[]>(options);
    return resp;
}

export async function updateAdminMessage(payload: IUpdateMessageRequest): Promise<IBookingApiResponse<IAdminMessage>> {
    const endPoint = bookingAPIPath.message.updateMessage();
    const options: IRequestOptions<IUpdateMessageRequest> = {
        path: endPoint.url,
        method: endPoint.method,
        body: payload
    };
    const resp = await request<IAdminMessage, IUpdateMessageRequest>(options);
    return resp;
}

// ----------------------------------------------------------------------
// Plan subscription API — admin
// ----------------------------------------------------------------------

export async function plansAdminCreate(payload: Omit<IPlan, 'id' | 'createdAt' | 'updatedAt'>): Promise<IBookingApiResponse<IPlan>> {
    const endPoint = bookingAPIPath.plans.adminCreate();
    const options: IRequestOptions<typeof payload> = { path: endPoint.url, method: endPoint.method, body: payload };
    return request<IPlan, typeof payload>(options);
}

export async function plansAdminListQuery(): Promise<IBookingApiResponse<IPlan[]>> {
    const endPoint = bookingAPIPath.plans.adminList();
    const options: IRequestOptions = { path: endPoint.url, method: endPoint.method };
    return request<IPlan[]>(options);
}

export async function plansAdminGetById(planId: string): Promise<IBookingApiResponse<IPlan>> {
    const endPoint = bookingAPIPath.plans.adminGetById(planId);
    const options: IRequestOptions = { path: endPoint.url, method: endPoint.method };
    return request<IPlan>(options);
}

export async function plansAdminUpdate(
    planId: string,
    payload: Partial<Omit<IPlan, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<IBookingApiResponse<boolean>> {
    const endPoint = bookingAPIPath.plans.adminUpdate(planId);
    const options: IRequestOptions<typeof payload> = { path: endPoint.url, method: endPoint.method, body: payload };
    return request<boolean, typeof payload>(options);
}

export async function plansAdminListSubscriptions(): Promise<IBookingApiResponse<IMerchantSubscriptionGlobal[]>> {
    const endPoint = bookingAPIPath.plans.adminListSubscriptions();
    const options: IRequestOptions = { path: endPoint.url, method: endPoint.method };
    return request<IMerchantSubscriptionGlobal[]>(options);
}

// Plan subscription API — merchant
export async function plansMerchantListQuery(): Promise<IBookingApiResponse<IPlan[]>> {
    const endPoint = bookingAPIPath.plans.merchantList();
    const options: IRequestOptions = { path: endPoint.url, method: endPoint.method };
    return request<IPlan[]>(options);
}

export async function plansMerchantGetSubscription(): Promise<IBookingApiResponse<IMerchantPlanSubscription>> {
    const endPoint = bookingAPIPath.plans.merchantGetSubscription();
    const options: IRequestOptions = { path: endPoint.url, method: endPoint.method };
    return request<IMerchantPlanSubscription>(options);
}

export async function plansMerchantSelectPlan(planId: string): Promise<IBookingApiResponse<IMerchantPlanSubscription>> {
    const endPoint = bookingAPIPath.plans.merchantSelectPlan();
    const options: IRequestOptions<ISelectPlanBody> = { path: endPoint.url, method: endPoint.method, body: { planId } };
    return request<IMerchantPlanSubscription, ISelectPlanBody>(options);
}

export default request;
